import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  makeStyles,
  Box,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import axios from "axios";
import { appUrl } from "../../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  actions: {
    justifyContent: "center",
    padding: theme.spacing(2),
    paddingTop: 0
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(4),
    textAlign: "center",
  },
  fieldLabel: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: "#000",
  },
  requiredStar: {
    color: "red",
  },
  select: {
    width: "100%",
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: 4,
    },
  },
  continueButton: {
    borderRadius: "2rem",
    padding: "8px 32px",
    backgroundColor: "#ec6535",
    color: "#fff",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d85a2f",
    },
  },
  cancelButton: {
    position: "absolute",
    right: theme.spacing(2),
    top: theme.spacing(2),
    color: "#000",
    fontWeight: "bold",
  },
}));

const ProductSelectionDialog = ({ open, onClose, onContinue, selectedSupplytype }) => {
  const classes = useStyles();
  const [selectedProduct, setSelectedProduct] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && selectedSupplytype) {
      fetchProducts();
    }
  }, [open, selectedSupplytype]);

  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");
  
    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, [
    localStorage.getItem("appName"),
    localStorage.getItem("network"),
    localStorage.getItem("collabType"),
    localStorage.getItem("roleName"),
  ]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${apiBaseUrl}/fetchAllProducts`,
        {
          searchField: "all",
          searchValue: null,
          productModality: "all",
          productType: "all",
          status: "all",
          supplyTypeID: selectedSupplytype.supplyTypeID,
          pageNumber: 0,
          pageSize: 0, // Fetch more products for the dropdown
        }
      );
      if (response.data && response.data.status) {
        // Filter only active products
        const activeProducts = response.data.productDetailsList.filter(
          product => product.dashboardStatus === "Active"
        );
        setProducts(activeProducts || []);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = (event) => {
    const selectedProductId = event.target.value;
    const selectedProductObj = products.find(p => p.collabrixProductID === selectedProductId);
    setSelectedProduct(selectedProductObj);
  };

  const handleContinue = () => {
    if (onContinue && selectedProduct) {
      onContinue(selectedProduct);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box position="relative">
        <Button onClick={onClose} className={classes.cancelButton}>
          Cancel
        </Button>
      </Box>
      <DialogContent className={classes.content}>
        <Typography
          variant="h5"
          sx={{ marginBottom: 0 }}
          className={classes.title}
        >
          Please select the product from the list for {selectedSupplytype?.supplyTypeName}
        </Typography>

        <Box width="100%">
          <Typography variant="body1" className={classes.fieldLabel}>
            Product Code / Description{" "}
            <span className={classes.requiredStar}>*</span>
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>          ) : (
            <TextField
              select
              variant="outlined"
              fullWidth
              value={selectedProduct?.collabrixProductID || ""}
              onChange={handleProductChange}
              className={classes.select}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) {
                    return <>Select from the list</>;
                  }
                  const product = products.find(p => p.collabrixProductID === selected);
                  return product ? `${product.sponsorProductCode} / ${product.sponsorProductDescription}` : '';
                }
              }}
            >
              <MenuItem value="" disabled>
                <>Select from the list</>
              </MenuItem>
              {products.map((product) => (
                <MenuItem
                  key={product.collabrixProductID}
                  value={product.collabrixProductID}
                >
                  {`${product.sponsorProductCode} / ${product.sponsorProductDescription}`}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!selectedProduct || loading}
          className={classes.continueButton}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSelectionDialog;
